/**
 * The first-login note at the top of the queue: "New to I2Q? ..." with the quick start,
 * the full training and "Not now". Shown only while the progress has loaded and none of
 * quick start done, course completed or note dismissed is set. No redirect: deep links
 * from mail keep working.
 *
 * A manager (`see_pilot`) does not quote, so the note is labelled for their sales people
 * and points at the manager page as well (MK-06).
 */

import { Alert, Button } from "@norican/react";
import { useState } from "react";
import { useNavigate } from "react-router";

import {
  isTrainingOffline,
  setLocalNudgeOff,
  useMe,
  usePutTrainingState,
  useTrainingSummary,
} from "@/lib/api";
import { hasCapability } from "@/lib/areas";
import { FEATURES } from "@/lib/features";
import { logger } from "@/lib/logger";

import { MANAGERS_PATH } from "./managers-page";

export const NUDGE_TEXT = "New to I2Q? The quick start takes 5 minutes.";
export const MANAGER_NUDGE_TEXT = "For your sales people: the quick start takes 5 minutes.";

export function TrainingNudge() {
  if (!FEATURES.training || !FEATURES.trainingNudge) {
    return null;
  }
  return <Nudge />;
}

function Nudge() {
  const summary = useTrainingSummary();
  const dismiss = usePutTrainingState();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const manager = hasCapability(me, "see_pilot");
  const [hidden, setHidden] = useState(false);
  if (hidden || !summary.loaded || !summary.showNudge) {
    return null;
  }
  return (
    <Alert tone="info" title={manager ? MANAGER_NUDGE_TEXT : NUDGE_TEXT} data-training-nudge>
      <div className="mt-2 flex flex-wrap gap-2">
        {manager ? (
          <Button size="sm" tone="accent" onClick={() => navigate(MANAGERS_PATH)}>
            What your team will see
          </Button>
        ) : (
          <Button size="sm" tone="accent" onClick={() => navigate("/training/quick-start")}>
            Start the quick start
          </Button>
        )}
        <Button size="sm" variant="outline" tone="neutral" onClick={() => navigate("/training")}>
          See the full training
        </Button>
        <Button
          size="sm"
          variant="ghost"
          tone="neutral"
          onClick={() => {
            setHidden(true);
            dismiss.mutate(
              { nudgeDismissed: true },
              {
                onError: (error) => {
                  logger.warn("[training] nudge dismiss", error);
                  if (isTrainingOffline(error)) {
                    setLocalNudgeOff();
                  }
                },
                onSuccess: () => setLocalNudgeOff(),
              },
            );
          }}
        >
          Not now
        </Button>
      </div>
    </Alert>
  );
}
